import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { breadcrumbSchema, webPageSchema, faqSchema } from '../lib/seo';
import { Button } from '../components/ui/button';
import { faqs as allFaqs } from '../data/content';

// FAQs specific to founder-led sales intent (subset of existing homepage FAQs)
const FLS_FAQ_KEYS = [
  'What is founder-led sales?',
  'When does founder-led sales stop working?',
  'How do I move away from founder-led sales?',
  'When should a startup hire its first salesperson?',
  'Do I need a Head of Sales yet?',
];
const FLS_FAQS = allFaqs.filter(f => FLS_FAQ_KEYS.includes(f.q));

export default function FounderLedSalesPage() {
  const path = '/founder-led-sales';
  const title = 'Founder-Led Sales — Scaling Beyond the Founder | Founder Growth Collective';
  const description = 'Founder-led sales explained: when it stops working, how to move beyond it, and how to build the sales function that lets a funded startup scale without the founder in every deal.';

  const bc = [
    { name: 'Home', path: '/' },
    { name: 'Founder-Led Sales', path },
  ];

  const schemas = [
    webPageSchema({ path, name: title, description }),
    breadcrumbSchema(bc),
    faqSchema(FLS_FAQS),
  ];

  return (
    <div className="bg-[#EBE4DE] text-[#2E2C28] min-h-screen">
      <SEO title={title} description={description} path={path} schemas={schemas} />
      <Navbar />

      <main className="pt-36 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Breadcrumbs items={bc} />

          <header className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4">
              <p className="sec-num text-sm tracking-[0.3em] uppercase">/ Founder-Led Sales</p>
            </div>
            <div className="lg:col-span-8">
              <h1 className="font-serif text-[40px] sm:text-[54px] lg:text-[72px] leading-[1.02] tracking-[-0.02em] text-[#2E2C28] font-light">
                A business cannot scale <span className="italic text-[#B5876C]">one founder-led deal</span> at a time.
              </h1>
              <p className="mt-8 text-lg lg:text-xl text-[#4a463f] font-light leading-relaxed max-w-3xl">
                Funded startups struggle with sales when the founder remains the only closer. Scaling is impossible when every lead requires your direct involvement. Founder Growth Collective builds the autonomous systems that empower your business to accelerate without you.
              </p>
            </div>
          </header>

          <section aria-labelledby="approach" className="mt-24 grid grid-cols-1 lg:grid-cols-12 gap-10 border-t border-[#d9d2ca] pt-16">
            <div className="lg:col-span-4">
              <h2 id="approach" className="font-serif text-2xl lg:text-3xl text-[#2E2C28] font-light">The approach</h2>
            </div>
            <div className="lg:col-span-8 space-y-6 text-base lg:text-lg text-[#4a463f] font-light leading-relaxed max-w-3xl">
              <p>Founder Growth Collective partners with a select group of funded founders to architect a bespoke sales function that reflects your actual market stage, not a generic template.</p>
              <p>The objective is straightforward: remove the founder from daily sales tasks. Structures are implemented that allow growth to continue independently.</p>
              <p>Adaptable growth frameworks replace one-size-fits-all methods. Your sales function is specifically engineered for your funding velocity and target market, meeting your unique operational demands.</p>
            </div>
          </section>

          <section aria-labelledby="related" className="mt-24 border-t border-[#d9d2ca] pt-16">
            <h2 id="related" className="font-serif text-2xl lg:text-3xl text-[#2E2C28] font-light mb-10">Where founders start</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <RelatedCard title="Sales Analysis & Implementation" to="/services/sales-diagnostic" body="A deep-dive audit and custom sales department built from the ground up." />
              <RelatedCard title="Revenue Process Design" to="/services/sales-framework-design" body="Engineering a repeatable sales cycle for consistent growth." />
              <RelatedCard title="Talent Strategy Plan" to="/services/sales-team-hiring" body="Mapping headcount and landing top-tier sales talent." />
            </div>
          </section>

          <section aria-labelledby="faqs" className="mt-24 border-t border-[#d9d2ca] pt-16">
            <h2 id="faqs" className="font-serif text-2xl lg:text-3xl text-[#2E2C28] font-light mb-8">Founder-led sales — questions we hear most</h2>
            <Accordion type="single" collapsible className="w-full">
              {FLS_FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-[#d9d2ca]">
                  <AccordionTrigger className="text-left py-5 hover:no-underline">
                    <span className="font-serif text-lg lg:text-xl text-[#2E2C28] font-light leading-snug">{f.q}</span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-base text-[#4a463f] font-light leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="mt-16">
            <Button asChild className="bg-[#2E2C28] hover:bg-[#B5876C] text-[#EBE4DE] rounded-none h-14 px-8 text-[12px] tracking-[0.2em] uppercase">
              <Link to="/contact" className="inline-flex items-center gap-3">Ready to scale past yourself? <ArrowUpRight size={16} strokeWidth={1.5} /></Link>
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function RelatedCard({ title, to, body }) {
  return (
    <Link to={to} className="group block border border-[#d9d2ca] hover:border-[#B5876C] p-6 transition-colors">
      <h3 className="font-serif text-xl text-[#2E2C28] font-light leading-tight group-hover:text-[#B5876C] transition-colors">{title}</h3>
      <p className="mt-3 text-sm text-[#57534b] font-light">{body}</p>
    </Link>
  );
}
